import { useMemo } from 'react';
import { useCurrentWorkspace } from '../../auth';
import { useCustomQuery } from '../../graphql';
import { WorkspaceAsset } from '../../assets/domain/workspaceAsset';
import { AssetChatSubject } from '../domain';

export type UseAssetChatSubjectArgs = {
  assetId?: string;
  /**
   * The workspace named by the route prefix
   * (`/workspaces/:workspaceId/asset/:assetId`), when there is one. It wins
   * over the viewer's active workspace.
   */
  routeWorkspaceId?: string;
  /**
   * Withholds the `workspaceasset` lookup — for a caller (the chat icon) that
   * mounts on every asset page but only needs the subject once the feature is
   * on for the workspace it already knows. Defaults to true.
   */
  enabled?: boolean;
};

/**
 * Resolves the chat's subject (see `AssetChatSubject`) before anything else
 * runs: the route's workspace if it names one, else the viewer's active
 * workspace, and if the asset has no `workspaceasset` row there the chat is
 * unavailable. This deliberately does NOT reuse `useWorkspaceAsset`, which
 * queries by asset id alone and reads the workspace off an arbitrary
 * `items[0]` of an unordered multi-workspace response.
 *
 * The lookup is a pre-check, not the access decision: `findIssues` verifies
 * membership of the explicit workspace itself and refuses non-members.
 */
export const useAssetChatSubject = ({
  assetId,
  routeWorkspaceId,
  enabled: isEnabledByCaller = true,
}: UseAssetChatSubjectArgs) => {
  const currentWorkspace = useCurrentWorkspace();
  // undefined = the account has not resolved yet; null = resolved with no
  // active workspace (My History). Only the former is still loading.
  const isResolvingWorkspace =
    !routeWorkspaceId && currentWorkspace === undefined;
  const workspaceId = routeWorkspaceId ?? currentWorkspace?.id;
  const enabled = isEnabledByCaller && !!assetId && !!workspaceId;

  const { data, isLoading, isFetching, isError, error, refetch } =
    useCustomQuery({
      query: FIND_ASSET_CHAT_SUBJECT,
      // Under useWorkspaceAsset's ['workspaceasset', assetId] prefix so its
      // invalidateQuery refreshes this row too.
      cacheKey: ['workspaceasset', assetId, 'chatSubject', workspaceId],
      variables: {
        filter: { asset: { eq: assetId }, workspace: { eq: workspaceId } },
      },
      options: { enabled },
    });

  const subject = useMemo<AssetChatSubject | undefined>(() => {
    // At most one live row exists per (asset, workspace) pair, so [0] here is
    // the pinned row, not a pick among workspaces.
    const row = data?.findWorkspaceAssets?.items?.[0];
    if (!row?.id || !assetId || !workspaceId) return undefined;
    return {
      workspaceAssetId: row.id,
      assetId,
      workspaceId,
      // The query selects the subset `hasAssetAccess` reads plus `asset.name`
      // (the chat column's title); cast once here, at the GraphQL boundary,
      // rather than at every consumer.
      workspaceAsset: row as WorkspaceAsset,
    };
  }, [data, assetId, workspaceId]);

  // react-query reports isLoading false for a disabled query, so the
  // workspace-resolution window is folded in explicitly.
  const isSubjectLoading = isResolvingWorkspace || (enabled && isLoading);

  return {
    subject,
    workspaceId,
    isLoading: isSubjectLoading,
    isError,
    isFetching,
    error,
    refetch,
    isUnavailable: !isSubjectLoading && !subject,
  };
};

export const FIND_ASSET_CHAT_SUBJECT = `
  query findAssetChatSubject($filter: WorkspaceAssetFilter) {
    findWorkspaceAssets(filter: $filter) {
      items {
        id
        asset {
          id
          name
        }
        workspace {
          id
        }
        groups {
          id
        }
        accounts {
          id
        }
      }
    }
  }
`;
